import { CommentsThreadSources, ShowCommentsThreadAction } from './commentsThread';
import { CONSTANTS } from './commentsThread_constants';
import { sanitizeSlateValue } from '../../common/TextEditor/utils/SlateSerialisationUtil';

export const COMMENTS_THREAD_SOURCES = {
    COMPANIES_LIST: 'COMPANIES_LIST',
    EDIT_PROFILE: 'EDIT_PROFILE',
    DASHBOARD: 'DASHBOARD',
    DEAL: 'DEAL',
    INVESTOR_DEAL: 'INVESTOR_DEAL',
    ONE_PAGER: 'ONE_PAGER',
    ENGAGEMENT_LETTER: 'ENGAGEMENT_LETTER',
    NDA: 'NDA',
    MANAGE_PARTNERS_LENDERS: 'MANAGE_PARTNERS_LENDERS',
    REPORTS: 'REPORTS',
} as const;

interface CommentsThreadReducer {
    showCommentsThread: boolean;
    source: CommentsThreadSources;
    loadingCommentsThread: boolean;
    closingCommentsThread: boolean;
    expandCommentsThread: boolean;

    headerText: string | null;
    headerSecondaryText?: string | null;

    assesseeOrgId?: string | null;
    dealId?: string | null;
    dealIdForInvestorDeal?: string | null;
    organizationId?: string | null;
    commentsThread: any[];

    refetchFlags: {
        refetchPartnersFlag: boolean;
    };
}

const initialState: CommentsThreadReducer = {
    showCommentsThread: false,
    source: null,
    loadingCommentsThread: false,
    closingCommentsThread: false,

    headerText: null,
    headerSecondaryText: null,
    expandCommentsThread: false,

    assesseeOrgId: null,
    dealId: null,
    dealIdForInvestorDeal: null,
    organizationId: null,
    commentsThread: [],

    refetchFlags: {
        refetchPartnersFlag: false,
    },
};

const setIsFetchingData = (state, action): CommentsThreadReducer => {
    return {
        ...state,
        loadingCommentsThread: action.payload,
    };
};

const setExpandCommentsThread = (state, action) => {
    return {
        ...state,
        expandCommentsThread: action.payload,
    };
};

const setShowCommentsThread = (state, action: ShowCommentsThreadAction): CommentsThreadReducer => {
    if (state.closingCommentsThread) return state;
    const { source } = action.payload;
    let newState = {
        ...state,
        source,
        showCommentsThread: true,
        assesseeOrgId: null,
        dealId: null,
        dealIdForInvestorDeal: null,
        organizationId: null,
        headerText: action.payload?.headerText ?? null,
        headerSecondaryText: action.payload?.headerSecondaryText ?? null,
    };

    if (
        source === 'COMPANIES_LIST' ||
        source === 'DASHBOARD' ||
        source === 'ONE_PAGER' ||
        source === 'EDIT_PROFILE' ||
        source === COMMENTS_THREAD_SOURCES.REPORTS
    ) {
        newState = { ...newState, assesseeOrgId: action.payload.assesseeOrgId };
    } else if (source === 'DEAL') {
        newState = { ...newState, dealId: action.payload.dealId, assesseeOrgId: action.payload.assesseeOrgId };
    } else if (source === 'INVESTOR_DEAL') {
        newState = {
            ...newState,
            dealId: action.payload.dealId,
            dealIdForInvestorDeal: action.payload.dealIdForInvestorDeal,
            assesseeOrgId: action.payload.assesseeOrgId,
        };
    } else if (source === 'MANAGE_PARTNERS_LENDERS') {
        newState = { ...newState, organizationId: action.payload.organizationId };
    }

    return newState;
};

const intitiateCloseCommentsThread = (state): CommentsThreadReducer => {
    return {
        ...state,
        closingCommentsThread: true,
        expandCommentsThread: false,
    };
};

const closeCommentsThreadAndReset = (): CommentsThreadReducer => {
    return { ...initialState };
};

const setCommentsThreadData = (state, action) => {
    // Sanitize textEditorValue in comments to handle old data structures.
    // Skip the clone entirely when there's no textEditorValue to migrate —
    // pass the comment object through unchanged.
    const sanitizedComments = action.payload.data
        ? action.payload.data.map((comment) =>
              Array.isArray(comment?.textEditorValue)
                  ? { ...comment, textEditorValue: sanitizeSlateValue(comment.textEditorValue) }
                  : comment,
          )
        : undefined;

    return {
        ...state,
        loadingCommentsThread: false,
        ...(sanitizedComments && { commentsThread: sanitizedComments }),
        ...(action.payload.headerText && { headerText: action.payload.headerText }),
    };
};

const addCommentToThread = (state, action) => {
    const { commentId } = action;
    //find if the commentId is present in the commentsThread
    let existingCommentUpdated = false;
    let updatedCommentsThread = state.commentsThread.map((comment) => {
        if (comment.commentId === commentId) {
            existingCommentUpdated = true;
            return {
                ...comment,
                ...action.payload,
            };
        }
        return comment;
    });
    if (!existingCommentUpdated)
        updatedCommentsThread.push({
            ...action.payload,
            commentId: commentId,
        });

    //write code here id commentId is present and not present in the commentsThread
    return {
        ...state,
        commentsThread: [...updatedCommentsThread],
    };
};

const removeCommentFromThread = (state, action) => {
    const { commentId } = action;
    // const updatedCommentsThread = state.commentsThread.filter((comment) => comment.commentId !== commentId);
    //USAGE DISABLED AS COMMENTS ARE BEING REFETCHED AFTER CHANGES
    return {
        ...state,
    };
};

const setRefetchFlag = (state, action) => {
    const { flagKey, status } = action.payload;
    return {
        ...state,
        refetchFlags: {
            ...state.refetchFlags,
            ...(flagKey && { [flagKey]: status || false }),
        },
    };
};

const reducer = (state = initialState, action): CommentsThreadReducer => {
    switch (action.type) {
        case CONSTANTS.SET_IS_FETCHING_DATA:
            return setIsFetchingData(state, action);
        case CONSTANTS.SET_SHOW_COMMENTS_THREAD:
            return setShowCommentsThread(state, action);
        case CONSTANTS.SET_EXPAND_COMMENTS_THREAD:
            return setExpandCommentsThread(state, action);
        case CONSTANTS.INITIATE_CLOSE_COMMENTS_THREAD:
            return intitiateCloseCommentsThread(state);
        case CONSTANTS.CLOSE_COMMENTS_THREAD:
            return closeCommentsThreadAndReset();
        case CONSTANTS.SET_COMMENTS_THREAD_DATA:
            return setCommentsThreadData(state, action);
        case CONSTANTS.ADD_COMMENT_TO_THREAD:
            return addCommentToThread(state, action);
        case CONSTANTS.REMOVE_COMMENT_FROM_THREAD:
            return removeCommentFromThread(state, action);
        case CONSTANTS.SET_REFETCH_FLAG:
            return setRefetchFlag(state, action);
        default:
            return state;
    }
};

export default reducer;
